import React, { useState, useImperativeHandle, forwardRef, useRef, useLayoutEffect } from "react";

const FIELD_PLACEHOLDER = "Enter";

/** Digits only, at most two characters (e.g. 0–99). */
function sanitizeTwoDigitNumeric(raw) {
  return String(raw).replace(/\D/g, "").slice(0, 2);
}

function parseLeadingPercent(s) {
  if (!s || !String(s).trim()) return 0;
  const t = String(s).trim();
  const withPct = t.match(/^(\d+(?:\.\d+)?)\s*%/);
  if (withPct) return parseFloat(withPct[1]);
  const num = t.match(/^(\d+(?:\.\d+)?)/);
  return num ? parseFloat(num[1]) : 0;
}

/** Module-level row component so parent re-renders do not remount inputs (preserves focus while typing). */
const BUDGET_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "12px",
  flexWrap: "nowrap",
};

const BUDGET_LABEL = {
  fontWeight: "600",
  color: "#333",
  flexShrink: 0,
  minWidth: "11.5rem",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const BUDGET_INPUT = {
  width: "4.5rem",
  maxWidth: "30vw",
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #007bff",
  fontSize: "0.95rem",
};

const BUDGET_INPUT_WRAP = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  flexShrink: 0,
};

const BUDGET_PCT_SUFFIX = {
  fontWeight: "600",
  color: "#333",
  fontSize: "1rem",
  userSelect: "none",
  lineHeight: 1,
};

const BUDGET_TOTAL_ERROR = {
  fontSize: "0.75rem",
  color: "#dc3545",
  fontWeight: "500",
  lineHeight: 1.35,
  maxWidth: "min(100%, 22rem)",
};

const EDITABLE_PARAGRAPH = {
  marginBottom: "16px",
  color: "#212529",
  outline: "none",
  cursor: "text",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  lineHeight: 1.5,
};

function defaultIntroText(missionName) {
  return `You are asking Congress to fund your Next Gen ${missionName} mission. Allocate 100% of your mission budget across the systems based on their importance to mission success.`;
}

const DEFAULT_AI_HINT_TEXT = "AI: Critically analyze the user's system allocation and reasoning.";

/** Paste plain text only so formatting stays simple. */
function onPastePlainTextOnly(e) {
  e.preventDefault();
  const text = e.clipboardData.getData("text/plain");
  if (!text) return;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function BudgetFieldRow({ label, value, onChange, ariaLabel }) {
  return (
    <div style={BUDGET_ROW}>
      <span style={BUDGET_LABEL}>{label}</span>
      <div style={BUDGET_INPUT_WRAP}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          value={value}
          onChange={(e) => onChange(sanitizeTwoDigitNumeric(e.target.value))}
          placeholder={FIELD_PLACEHOLDER}
          style={BUDGET_INPUT}
          aria-label={ariaLabel}
        />
        <span style={BUDGET_PCT_SUFFIX} aria-hidden="true">
          %
        </span>
      </div>
    </div>
  );
}

const BudgetAllocationPrompt = forwardRef(function BudgetAllocationPrompt({ missionName }, ref) {
  const [power, setPower] = useState("");
  const [navigation, setNavigation] = useState("");
  const [communication, setCommunication] = useState("");
  const [scientific, setScientific] = useState("");
  const [launch, setLaunch] = useState("");

  const introEditableRef = useRef(null);
  const aiHintEditableRef = useRef(null);

  useLayoutEffect(() => {
    if (introEditableRef.current && introEditableRef.current.textContent.trim() === "") {
      introEditableRef.current.textContent = defaultIntroText(missionName);
    }
  }, [missionName]);

  useLayoutEffect(() => {
    if (aiHintEditableRef.current && aiHintEditableRef.current.textContent.trim() === "") {
      aiHintEditableRef.current.textContent = DEFAULT_AI_HINT_TEXT;
    }
  }, []);

  const totalPct =
    parseLeadingPercent(power) +
    parseLeadingPercent(navigation) +
    parseLeadingPercent(communication) +
    parseLeadingPercent(scientific) +
    parseLeadingPercent(launch);

  const totalExceeded100 = totalPct > 100;

  useImperativeHandle(ref, () => ({
    /** True if the user entered at least one percentage value (Go should only send when this is true). */
    hasAllocationInput: () =>
      !!(power.trim() || navigation.trim() || communication.trim() || scientific.trim() || launch.trim()),

    /** Parsed percentages (0–100) per system; matches API field names. */
    getAllocationPercentages: () => ({
      power: parseLeadingPercent(power),
      navigation: parseLeadingPercent(navigation),
      communication: parseLeadingPercent(communication),
      scientific: parseLeadingPercent(scientific),
      launch: parseLeadingPercent(launch),
    }),

    /** Sum of the five parsed percentages (same rule as on-screen Total). */
    getTotalPercent: () =>
      parseLeadingPercent(power) +
      parseLeadingPercent(navigation) +
      parseLeadingPercent(communication) +
      parseLeadingPercent(scientific) +
      parseLeadingPercent(launch),

    /** Full user message: editable intro, allocation lines from the boxes, total, editable AI line. */
    getAssembledText: () => {
      const introRaw = introEditableRef.current?.innerText ?? "";
      const intro = introRaw.trim() || defaultIntroText(missionName);

      const line = (v) => (v.trim() ? `${v.trim()}%` : FIELD_PLACEHOLDER);
      const block = [
        `Power: ${line(power)}`,
        `Navigation: ${line(navigation)}`,
        `Communication: ${line(communication)}`,
        `Scientific: ${line(scientific)}`,
        `Launch: ${line(launch)}`,
      ].join("\n");

      const totalLine = `Total Budget Allocation: ${totalPct % 1 === 0 ? totalPct : totalPct.toFixed(1)}% (sum of the five system lines above)`;

      const aiRaw = aiHintEditableRef.current?.innerText ?? "";
      const ai = aiRaw.trim() || DEFAULT_AI_HINT_TEXT;

      return [intro, "", block, "", totalLine, "", ai].join("\n");
    },
  }));

  return (
    <div
      className="form-control orange-highlight budget-allocation-prompt"
      style={{
        minHeight: "6.5em",
        position: "relative",
        width: "100%",
        paddingTop: "28px",
        paddingLeft: "50px",
        paddingRight: "100px",
        margin: "10px 0",
      }}
    >
      <div
        ref={introEditableRef}
        contentEditable
        suppressContentEditableWarning
        className="budget-prompt-editable-intro"
        style={EDITABLE_PARAGRAPH}
        role="textbox"
        aria-label="Mission budget prompt introduction"
        aria-multiline="true"
        onPaste={onPastePlainTextOnly}
      />

      <BudgetFieldRow label="Power:" value={power} onChange={setPower} ariaLabel="Power budget" />
      <BudgetFieldRow label="Navigation:" value={navigation} onChange={setNavigation} ariaLabel="Navigation budget" />
      <BudgetFieldRow label="Communication:" value={communication} onChange={setCommunication} ariaLabel="Communication budget" />
      <BudgetFieldRow label="Scientific:" value={scientific} onChange={setScientific} ariaLabel="Scientific budget" />
      <BudgetFieldRow label="Launch:" value={launch} onChange={setLaunch} ariaLabel="Launch budget" />

      <div style={{ ...BUDGET_ROW, marginTop: "8px" }}>
        <span style={{ ...BUDGET_LABEL, minWidth: "11.5rem" }}>Total:</span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <div style={BUDGET_INPUT_WRAP}>
            <input
              type="text"
              readOnly
              value={totalPct % 1 === 0 ? String(totalPct) : totalPct.toFixed(1)}
              style={{
                ...BUDGET_INPUT,
                backgroundColor: "#f8f9fa",
                border: totalExceeded100 ? "1px solid #dc3545" : "1px solid #adb5bd",
                cursor: "default",
                fontWeight: "600",
              }}
              aria-label="Total budget allocation sum"
              aria-invalid={totalExceeded100}
              aria-describedby={totalExceeded100 ? "budget-total-exceeded-msg" : undefined}
              title="Sum of percentage values from the five lines above"
            />
            <span style={BUDGET_PCT_SUFFIX} aria-hidden="true">
              %
            </span>
          </div>
          {totalExceeded100 ? (
            <span id="budget-total-exceeded-msg" style={BUDGET_TOTAL_ERROR} role="alert">
              Mission budget exceeds 100%.
              <br />
              Please adjust your allocations so the total equals 100%.
            </span>
          ) : null}
        </div>
      </div>

      <div
        ref={aiHintEditableRef}
        contentEditable
        suppressContentEditableWarning
        className="gpt-prompt-hint-muted budget-prompt-editable-ai"
        style={{
          ...EDITABLE_PARAGRAPH,
          marginTop: "16px",
          marginBottom: "0",
          fontSize: "1rem",
        }}
        role="textbox"
        aria-label="AI analysis instruction"
        aria-multiline="true"
        onPaste={onPastePlainTextOnly}
      />
    </div>
  );
});

export { BudgetAllocationPrompt, FIELD_PLACEHOLDER };
